import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { getUserTopicStudyPlanStatus } from '@/store/study-plan/actions';

import { selectStudyPlan as studyPlanSelect } from '@/store/study-plan/selectors';

import { Button, message, Tooltip } from 'antd';
import { HeartFilled, HeartOutlined } from '@ant-design/icons';

import MaterialButton from '../MaterialButton';
import Rate from '../Rate';
import RepeatDisclaimer from '../RepeatDisclaimer';
import TypeBadge from '../TypeBadge';
import { MaterialCost } from './MaterialCost';

import useImage from '@shared/hooks/useImage';
import {
  isBook,
  isConference,
  isCorpCourseWithOrWithoutApprove,
  isCourse,
  isEvent,
  isExternalCourse,
} from '@shared/utils/topic-utils';

import Utils from '@/Utils';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _get from 'lodash/get';
import { noun } from 'plural-ru';
import PropTypes from 'prop-types';

import css from './MaterialCard.module.scss';

import { ADD_REPEAT } from '@/constants/studyPlanStatus';
import { TOPIC_FIELD, TOPIC_TYPES } from '@/constants/topic-types';
import FireBadge from '@/assets/images/fire-badge.svg';
import RecommendedBadge from '@/assets/images/recommended-flag.svg';

const MaterialCard = props => {
  const {
    topic,
    onDeleteClick,
    className,
    domainCompany,
    inMyCollection,
    onClick,
    isExternalLogin,
    onDeleteClickCatalog,
    userTopicId,
    currentUser,
    onFavoriteClick,
    onFavoriteClickCatalog,
    onStudyPlanClick,
  } = props;

  const { t } = useTranslation(['materialCard', 'constants']);
  const dispatch = useDispatch();

  const headerRef = useRef(null);

  const [deleted, setDeleted] = useState(false);

  const [correctFullDate, setCorrectFullDate] = useState('');

  const { newStatusPlanId, topicStudyPlanStatus } = useSelector(studyPlanSelect);
  const [checkRetake, setCheckRetake] = useState({});
  const [isEllipsis, setIsEllipsis] = useState(false);

  const isBlocked = topic.scope !== 'EXTERNAL' && isExternalLogin;

  const thumbnailUuid = isCorpCourseWithOrWithoutApprove(topic?.type?.id)
    ? topic?.thumbFileUuid || currentUser?.domainCompany?.corpCourseBackground
    : topic?.thumbFileUuid;

  const defaultImg = props?.topic?.imageIDB ?? useImage(thumbnailUuid, 'topic', isBlocked ? '404' : topic?.type?.id);

  useEffect(() => {
    if (!!newStatusPlanId && newStatusPlanId?.topic?.id === topic.id) {
      dispatch(
        getUserTopicStudyPlanStatus(
          {
            userId: currentUser?.id,
            topicId: topic.id,
            isAdmin: false,
          },
          res => {
            setCheckRetake({ userPlanType: res?.type });
          }
        )
      );
    }
  }, [newStatusPlanId]);

  useEffect(() => {
    if (topic.startDate && topic.startTime) {
      setCorrectFullDate(
        dayjs(`${topic.startDate}T${topic.startTime}`).add(
          currentUser.timezone.offset / 60 - domainCompany?.timezone?.offset / 60,
          'h'
        )
      );
    }
  }, [topic.startDate, topic.startTime]);

  const handleFavoriteClick = () => {
    if (onFavoriteClick) {
      if (props.inMyCollection || props.isRecommended) {
        onFavoriteClick(props.currentUser.id, topic.id, () => {
          setDeleted(false);
        });
      } else {
        onFavoriteClickCatalog(topic.id);
        setDeleted(false);
      }
    }
  };

  const retakeTopic = checkRetake?.userPlanType
    ? topicStudyPlanStatus.type === ADD_REPEAT
    : topic?.userPlanType === ADD_REPEAT;

  const handleStudyPlanClick = async () => {
    const disclaimerResult = retakeTopic && (await RepeatDisclaimer.Modal());

    if (disclaimerResult || !retakeTopic) {
      if (topic.isTopicFull) {
        message.warning(t('topicFull'));
        return;
      }

      onStudyPlanClick(topic.id, topic.type, topic.startDate, {
        id: topic.id,
        independent: topic.independent,
        startDate: topic.startDate,
        topicCost: topic.cost,
        topicDates: Array.isArray(topic.dates) && topic.dates.map(date => ({ dates: date.startDate })),
        link: topic?.firstLink || topic?.link || (topic?.links && topic?.links?.[0]?.url),
        eventCount: topic.futureEvents,
        topicComment: topic.comment,
      });
    }
  };

  const handleDeleteClick = () => {
    if (onDeleteClick) {
      if (props.inMyCollection || props.isRecommended) {
        onDeleteClick(props.topic && props.topic.id, true, true, () => {
          setDeleted(true);
        });
      } else {
        onDeleteClickCatalog(userTopicId);
        setDeleted(true);
      }
    }
  };

  const setDuration = mins => {
    const hours = Math.trunc(mins / 60);
    const minutes = mins % 60;

    if (hours === 0) {
      return `${minutes} ${t('minutes')}`;
    }
    if (minutes > 0 && hours !== 0) {
      return `${hours} ${noun(hours, t('hour1'), t('hour2'), t('hour3'))} ${minutes} ${t('minutes')}`;
    }
    return `${hours} ${noun(hours, t('hour1'), t('hour2'), t('hour3'))}`;
  };

  const isInternalScope = !isExternalLogin && topic.scope === 'INTERNAL';

  const toMyCollectionButton =
    !deleted && (inMyCollection || topic.isFavourite || props.isFav) ? (
      <Button
        className={classNames(css['App-btn'], css['App-btn-ghost'], css['App-favorite'], {
          [css['single-heart-position']]: isInternalScope,
        })}
        type='link'
        onClick={handleDeleteClick}
        icon={<HeartFilled className={css['App-btn-icon']} />}
        size='large'
      ></Button>
    ) : (
      <Button
        className={classNames(css['App-btn'], css['App-btn-ghost'], {
          [css['single-heart-position']]: isInternalScope,
        })}
        onClick={handleFavoriteClick}
        type='link'
        icon={<HeartOutlined className={css['App-btn-icon']} />}
        size='large'
      ></Button>
    );

  const topicType = topic.type && TOPIC_TYPES(t).find(type => type.id === topic.type.id);

  const typeBadge = topicType ? <TypeBadge type={topicType.code} name={topic.type.name} isMaterialCard={true} /> : null;

  const typeField = topic.type && TOPIC_FIELD(t).find(type => type.id === topic.type.id);
  const typeFieldRate =
    typeField &&
    typeField.fields.find(field => {
      const { type } = field;
      if (topic[type] && (topic[type].length || topic[type] > 0)) {
        return field.type;
      }
    });
  const topicSubType = topic?.subType?.id;

  const truncatedName = topic.name && Utils.trString(topic.name, 67);
  const rate = <Rate count={5} value={Math.round(topic.rate)} disabled />;
  const typeFieldRateLabel = typeFieldRate ? typeFieldRate.name : null;
  const tooltipTypeFieldRateValue = typeFieldRateLabel
    ? typeFieldRate.type === 'authors'
      ? topic[typeFieldRate.type].length > 1
        ? topic[typeFieldRate.type].map(name => name.name)
        : null
      : null
    : null;
  const typeFieldRateValue = typeFieldRateLabel ? (
    typeFieldRate.type === 'authors' ? (
      topic[typeFieldRate.type].length > 1 ? (
        <Tooltip title={tooltipTypeFieldRateValue.join(', ')}>
          {`${topic[typeFieldRate.type][0].name} ${t('other')}`}
        </Tooltip>
      ) : (
        <Tooltip title={topic[typeFieldRate.type][0].name}>{topic[typeFieldRate.type][0].name}</Tooltip>
      )
    ) : typeFieldRate.type === 'startDate' ? (
      dayjs(topic[typeFieldRate.type]).format('DD MMMM YYYY')
    ) : (
      topic[typeFieldRate.type]
    )
  ) : null;

  // СТОИМОСТЬ {showCost(topic.cost.toLocaleString())}
  const cost =
    topic &&
    topic.type &&
    topic.cost &&
    (isCorpCourseWithOrWithoutApprove(topic.type?.id) ||
      isEvent(topic.type.id) ||
      isBook(topic.type.id) ||
      isExternalCourse(topic.type.id) ||
      isConference(topic.type.id)) ? (
      <div className={css['MaterialCard-costLabel']}>
        <MaterialCost topic={topic} currentUser={currentUser} />
      </div>
    ) : null;

  const fixMaterial = topic ? (
    <div className={css['MaterialCard-fixMaterial']}>
      {props.isRecommended && (
        <img src={RecommendedBadge} alt={t('spacialForYou')} className={css['MaterialCard-fixMaterial-icon']} />
      )}
      {!!topic.position && (
        <img src={FireBadge} alt='fix' className={classNames(css['MaterialCard-fixMaterial-fire'])} />
      )}
    </div>
  ) : null;

  const header =
    onClick || isInternalScope ? (
      <div className={classNames(css.header, css.nonePointer)} data-qa='materialCardTopicName' ref={headerRef}>
        {truncatedName}
      </div>
    ) : (
      <Link
        to={`/collection-topics/${props.topic.id}`}
        className={css.header}
        data-qa='materialCardTopicName'
        ref={headerRef}
      >
        {truncatedName}
      </Link>
    );

  useEffect(() => {
    const el = headerRef.current;
    if (el) {
      setIsEllipsis(el.scrollHeight > el.clientHeight + 1);
    }
  }, [topic.name]);

  return (
    <div className={classNames(className, css.MaterialCard, css['App-card-item'])} onClick={onClick}>
      {onClick || isInternalScope ? (
        <div className={css['MaterialCard-image']}>
          <img src={defaultImg} alt='' />
        </div>
      ) : (
        <Link to={`/collection-topics/${props.topic.id}`}>
          <div
            className={classNames(css['MaterialCard-image'], css.ImageLink)}
            style={{ backgroundColor: topic?.thumbFileUuid ? topic.coverColor : '#efefef' }}
          >
            <div
              className={classNames(css['MaterialCard-image'], css.ImageLink, {
                [css.hasBg]: topic?.thumbFileUuid && topic?.coverColor?.length > 0,
              })}
              style={{
                backgroundColor: topic?.thumbFileUuid ? topic.coverColor : '#efefef',
              }}
            >
              <img src={defaultImg} alt='' />
            </div>
            {fixMaterial}
            {cost}
          </div>
        </Link>
      )}
      <div className={css['MaterialCard-block']}>
        {topicSubType === 21 || topicSubType === 18 || topicSubType === 37 ? (
          <p className={css.headerSubType} style={{ color: '#4bbd5c' }}>
            {t('online')}
          </p>
        ) : topicSubType === 22 || topicSubType === 19 || topicSubType === 36 ? (
          <p className={css.headerSubType} style={{ color: '#05b' }}>
            {t('fullTime')}
          </p>
        ) : null}
        {
          // topic.name.length > 67
          isEllipsis ? <Tooltip title={topic.name}>{header}</Tooltip> : header
        }
        {isInternalScope ? (
          <div className={css['MaterialCard-container-personal']}>
            {!props.hideActionButtons ? (
              <div
                className={classNames(className, css['MaterialCard-personal'], css['MaterialCard-personal-internal'])}
              >
                <div className={css.labelNotContent}>{t('blocked')}</div>
              </div>
            ) : (
              <div
                className={classNames(className, css['MaterialCard-personal'], css['MaterialCard-personal-internal2'])}
              >
                <div className={css.labelNotContent}>{t('blocked')}</div>
              </div>
            )}
            {toMyCollectionButton}
          </div>
        ) : (
          <div className={css['MaterialCard-container-personal']}>
            <div className={css['MaterialCard-personal']}>
              <div className={css.row}>
                <div className={css.label}>{t('type')}</div>
                <div className={css.value}>{typeBadge}</div>
              </div>
              <div className={css.row}>
                <div className={css.label}>{t('rate')}</div>
                <div className={css.value}>{rate}</div>
              </div>
              <div className={css.row}>
                <div className={css.label}>
                  {!isConference(topic.type.id) && !isEvent(topic.type.id)
                    ? topic.duration
                      ? isExternalCourse(topic.type.id) ||
                        isCorpCourseWithOrWithoutApprove(topic.type.id) ||
                        isCourse(topic.type.id)
                        ? t('capacity')
                        : t('duration')
                      : typeFieldRateLabel
                    : t('startDate')}
                </div>
                <div className={css.value}>
                  {!isConference(topic.type.id) && !isEvent(topic.type.id)
                    ? topic.duration
                      ? setDuration(topic.duration)
                      : typeFieldRateValue
                    : dayjs(correctFullDate).format('DD.MM.YYYY')}
                </div>
              </div>
            </div>
            {!props.offline && <MaterialButton topic={topic} />}
          </div>
        )}
      </div>
      <RepeatDisclaimer />
    </div>
  );
};

MaterialCard.propTypes = {
  className: PropTypes.string,
  currentUser: PropTypes.object,
  domainCompany: PropTypes.object,
  isExternalLogin: PropTypes.bool,
  hideActionButtons: PropTypes.bool,
  inMyCollection: PropTypes.bool,
  isFav: PropTypes.bool,
  onClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onDeleteClickCatalog: PropTypes.func,
  onFavoriteClick: PropTypes.func,
  onFavoriteClickCatalog: PropTypes.func,
  onStudyPlanClick: PropTypes.func,
  topic: PropTypes.object,
  userTopicId: PropTypes.number,
  isRecommended: PropTypes.bool,
};

export default MaterialCard;
